import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Link,
  Stack,
  Divider,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { toast } from 'sonner';
import { useAnalyticsStore } from '../../stores/analyticsStore';
import { STRATEGY_CONTENT_MAX } from '../../types/analytics';

const StrategyEditor: React.FC = () => {
  const {
    strategy,
    strategyLoading,
    strategyHistory,
    strategyHistoryLoading,
    loadStrategy,
    updateStrategy,
    loadStrategyHistory,
  } = useAnalyticsStore();

  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Remember the most recent server value we synced into the draft. If a
  // background re-fetch lands while the user is mid-edit (draft !== previous
  // server value), we must NOT overwrite their work.
  const lastSyncedRef = useRef<string | null>(null);

  useEffect(() => {
    loadStrategy().catch(() => {
      /* error already logged in store */
    });
  }, [loadStrategy]);

  useEffect(() => {
    if (!strategy) return;
    const serverContent = strategy.content ?? '';
    // Only sync when the draft isn't dirty relative to what we previously
    // synced — first render (null ref) or user hasn't touched it.
    if (lastSyncedRef.current === null || draft === lastSyncedRef.current) {
      setDraft(serverContent);
      lastSyncedRef.current = serverContent;
    } else {
      // User has unsaved edits. Update the ref so the next save's dirty
      // check compares against the fresh server value, but leave the draft
      // alone.
      lastSyncedRef.current = serverContent;
    }
    // We intentionally depend only on `strategy` — adding `draft` would
    // cause this effect to fire on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strategy]);

  const overLimit = draft.length > STRATEGY_CONTENT_MAX;
  const dirty = strategy ? draft !== (strategy.content ?? '') : draft.length > 0;
  const canSave = dirty && !overLimit && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await updateStrategy(draft);
      // Mark the saved value as the new synced baseline so the effect sees
      // draft === lastSyncedRef when the store's `strategy` change fires.
      lastSyncedRef.current = draft;
      toast.success('Strategy updated');
    } catch (err) {
      console.error('Failed to update strategy:', err);
      toast.error('Failed to update strategy');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    try {
      await loadStrategyHistory();
    } catch {
      toast.error('Failed to load strategy history');
    }
  };

  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Strategic Context
        </Typography>
        <Link
          component="button"
          type="button"
          onClick={openHistory}
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 12,
            textDecoration: 'none',
          }}
        >
          <HistoryIcon sx={{ fontSize: 14 }} />
          View history
        </Link>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Plain-text strategy notes consumed by the weekly insights generator. Keep it concise — the
        model reads this verbatim.
      </Typography>

      {strategyLoading ? (
        <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
      ) : (
        <TextField
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Targeting mid-sized utilities in the DACH region. Q4 priority is enterprise positioning..."
          multiline
          minRows={5}
          maxRows={12}
          fullWidth
          error={overLimit}
          helperText={`${draft.length}/${STRATEGY_CONTENT_MAX}${overLimit ? ' — too long' : ''}`}
          inputProps={{ 'data-testid': 'strategy-content-input' }}
        />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
        <Button
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={!canSave}
          data-testid="strategy-save-btn"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Strategy history</DialogTitle>
        <DialogContent dividers>
          {strategyHistoryLoading ? (
            <Stack spacing={1}>
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} variant="rectangular" height={64} />
              ))}
            </Stack>
          ) : strategyHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No prior versions yet.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {strategyHistory.map((entry, i) => (
                <Box key={entry.id}>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(entry.updated_at).toLocaleString()}
                    {entry.updated_by ? ` · ${entry.updated_by}` : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', mt: 0.25, fontSize: 13 }}
                  >
                    {entry.content || '(empty)'}
                  </Typography>
                  {i < strategyHistory.length - 1 && <Divider sx={{ mt: 1.5 }} />}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default StrategyEditor;
